import React, { ChangeEvent, KeyboardEvent, memo, useEffect, useId, useMemo, useRef, useState } from 'react';
import { BiHome, BiUser } from 'react-icons/bi';
import { LuArrowUpDown, LuBuilding2, LuCornerDownLeft, LuRefreshCw, LuSparkle } from 'react-icons/lu';
import { RiAccountBoxLine } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/utils/cn';
import { useAiChatSidebarState } from '@/views/ai-chat/ai-chat-state';
import { useAiChatNewConversation } from '@/views/ai-chat/useAiChatNewConversation';
import { useSearchStream } from '@/views/tools/useSearchStream';

import {
    DEFAULT_RECENT_STORAGE_KEY,
    loadRecentSearches,
    recentSearchChangeEvent,
    saveRecentSearch,
} from './recent-search-store';
import {
    buildSearchMapping,
    EMPTY_SEARCH_MAPPING,
    getActiveSearchItemIndex,
    getGroupForTab,
    getSearchOptionId,
    getSearchSelectableItemKey,
    SearchEntityType,
    SearchGroup,
    SearchResultItem,
    SearchSelectableItem,
    SearchTab,
} from './universal-search-utils';

const getItemKeyAtIndex = (items: SearchSelectableItem[], index: number) => {
    const item = index >= 0 ? items[index] : undefined;
    return item ? getSearchSelectableItemKey(item) : null;
};

const ENTITY_DISPLAY: Record<SearchEntityType, { label: string; icon: React.ReactNode }> = {
    Household: { label: 'Households', icon: <BiHome /> },
    Person: { label: 'Persons', icon: <BiUser /> },
    Organization: { label: 'Organizations', icon: <LuBuilding2 /> },
    Account: { label: 'Accounts', icon: <RiAccountBoxLine /> },
};

export interface UseUniversalSearchResult {
    search: string;
    open: boolean;
    activeTab: SearchTab;
    activeIndex: number;
    dropdownId: string;
    tabPanelId: string;
    activeDescendantId?: string;
    announcement: string;
    query: string;
    groups: SearchGroup[];
    totalResults: number;
    recentItems: SearchResultItem[];
    visibleGroups: SearchGroup[];
    visibleItems: SearchSelectableItem[];
    isLoading: boolean;
    error: Error | null;
    showEmptyState: boolean;
    showFirstQuerySkeleton: boolean;
    showRefreshingProgress: boolean;
    showNoResults: boolean;
    handleOpenChange: (value: boolean) => void;
    handleAskAi: () => void;
    handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
    handleClear: () => void;
    handleFocus: () => void;
    handleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
    handleRetry: () => void;
    handleTabChange: (nextTab: SearchTab) => void;
    handleRowHover: (index: number) => void;
    handleSelectItem: (item: SearchSelectableItem, index: number) => void;
}

export const useUniversalSearch = (options?: { recentStorageKey?: string }): UseUniversalSearchResult => {
    const recentStorageKey = options?.recentStorageKey ?? DEFAULT_RECENT_STORAGE_KEY;

    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<SearchTab>('All');
    const [activeIndex, setActiveIndex] = useState(-1);
    const [recentItems, setRecentItems] = useState<SearchResultItem[]>(() => loadRecentSearches(recentStorageKey));
    const [announcement, setAnnouncement] = useState('');

    const activeItemKeyRef = useRef<string | null>(null);

    const navigate = useNavigate();
    const dropdownId = useId();
    const tabPanelId = `${dropdownId}-panel`;
    const { open: openAiChat } = useAiChatSidebarState();
    const { startNewConversation } = useAiChatNewConversation();

    const query = search.trim();
    const showEmptyState = query.length === 0;

    const { results, isLoading, error, retry } = useSearchStream(search, {
        enabled: !showEmptyState,
        debounceMs: 180,
        keepPreviousResults: true,
    });

    const mapping = showEmptyState ? EMPTY_SEARCH_MAPPING : buildSearchMapping(results);
    const groups = mapping.groups;
    const totalResults = mapping.totalResults;
    const currentIntentSignal = mapping.intentSignal?.prompt === query ? mapping.intentSignal : null;
    const isStrongAiQuestion =
        currentIntentSignal?.intent === 'AI_QUESTION' && currentIntentSignal.strength === 'STRONG';
    const currentTabGroup = useMemo(
        () => (activeTab === 'All' ? null : getGroupForTab(groups, activeTab)),
        [activeTab, groups],
    );
    const visibleGroups = useMemo(() => {
        if (showEmptyState || !open) {
            return [];
        }

        if (activeTab === 'All') {
            return groups.filter((group) => group.total > 0);
        }

        return currentTabGroup && currentTabGroup.total > 0 ? [currentTabGroup] : [];
    }, [activeTab, currentTabGroup, groups, open, showEmptyState]);
    const visibleItems = useMemo(
        () => (showEmptyState ? [...recentItems] : visibleGroups.flatMap((group) => group.items)),
        [recentItems, showEmptyState, visibleGroups],
    );

    const activeItem = activeIndex >= 0 ? visibleItems[activeIndex] : undefined;
    const activeDescendantId = open && activeItem ? getSearchOptionId(activeItem) : undefined;
    const showFirstQuerySkeleton = !showEmptyState && isLoading && totalResults === 0;
    const showRefreshingProgress = !showEmptyState && isLoading && totalResults > 0;
    const showNoResults =
        !showEmptyState &&
        !isLoading &&
        !error &&
        (activeTab === 'All' ? totalResults === 0 : (currentTabGroup?.total ?? 0) === 0);

    useEffect(() => {
        const reload = () => setRecentItems(loadRecentSearches(recentStorageKey));
        const onStorage = (event: StorageEvent) => {
            if (event.storageArea === window.localStorage && (event.key === recentStorageKey || event.key === null)) {
                reload();
            }
        };
        const eventName = recentSearchChangeEvent(recentStorageKey);
        reload();
        window.addEventListener(eventName, reload);
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener(eventName, reload);
            window.removeEventListener('storage', onStorage);
        };
    }, [recentStorageKey]);

    useEffect(() => {
        if (!query || isLoading || error) {
            setAnnouncement('');
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setAnnouncement(`${totalResults} results for ${query}`);
        }, 400);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [error, isLoading, query, totalResults]);

    useEffect(() => {
        if (!open) {
            return;
        }

        setActiveIndex((currentIndex) => {
            const nextIndex = getActiveSearchItemIndex({
                activeItemKey: activeItemKeyRef.current,
                currentIndex,
                items: visibleItems,
            });
            activeItemKeyRef.current = getItemKeyAtIndex(visibleItems, nextIndex);
            return nextIndex;
        });
    }, [open, visibleItems]);

    const handleSelectResult = (item: SearchSelectableItem) => {
        activeItemKeyRef.current = null;
        setOpen(false);
        setSearch('');
        setActiveTab('All');
        setActiveIndex(-1);

        if (item.kind === 'suggested') {
            navigate(item.href);
            return;
        }

        setRecentItems(saveRecentSearch(recentStorageKey, item));
        navigate(item.href);
    };

    const handleOpenChange = (value: boolean) => {
        setOpen(value);
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.currentTarget.value;
        setSearch(nextValue);
        setOpen(true);
        setActiveTab('All');
        activeItemKeyRef.current = null;
        setActiveIndex(0);
    };

    const handleFocus = () => {
        setOpen(true);
    };

    const handleClear = () => {
        setSearch('');
        setActiveTab('All');
        activeItemKeyRef.current = null;
        setActiveIndex(-1);
    };

    const handleAskAi = () => {
        if (!query) {
            return;
        }

        activeItemKeyRef.current = null;
        setOpen(false);
        setSearch('');
        setActiveIndex(-1);
        openAiChat('sidekick');
        startNewConversation(query);
    };

    const handleTabChange = (nextTab: SearchTab) => {
        if (nextTab !== 'All' && getGroupForTab(groups, nextTab).total === 0) {
            return;
        }

        activeItemKeyRef.current = null;
        setActiveTab(nextTab);
        setActiveIndex(0);
    };

    const handleRetry = () => {
        retry();
    };

    const handleRowHover = (index: number) => {
        activeItemKeyRef.current = getItemKeyAtIndex(visibleItems, index);
        setActiveIndex(index);
    };

    const handleSelectItem: UseUniversalSearchResult['handleSelectItem'] = (item) => {
        handleSelectResult(item);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Tab' && open && !showEmptyState) {
            const nonEmptyTabs: SearchTab[] = [
                'All',
                ...groups.filter((group) => group.total > 0).map((group) => group.type),
            ];

            if (nonEmptyTabs.length > 1) {
                event.preventDefault();
                const currentIndex = nonEmptyTabs.indexOf(activeTab);
                const direction = event.shiftKey ? -1 : 1;
                const nextIndex = (currentIndex + direction + nonEmptyTabs.length) % nonEmptyTabs.length;
                handleTabChange(nonEmptyTabs[nextIndex]);
            }

            return;
        }

        if (event.key === 'ArrowDown' && open) {
            event.preventDefault();
            setActiveIndex((currentIndex) => {
                if (visibleItems.length === 0) {
                    activeItemKeyRef.current = null;
                    return -1;
                }

                const nextIndex = Math.min(currentIndex + 1, visibleItems.length - 1);
                activeItemKeyRef.current = getItemKeyAtIndex(visibleItems, nextIndex);
                return nextIndex;
            });
            return;
        }

        if (event.key === 'ArrowUp' && open) {
            event.preventDefault();
            setActiveIndex((currentIndex) => {
                if (visibleItems.length === 0) {
                    activeItemKeyRef.current = null;
                    return -1;
                }

                const nextIndex = Math.max(currentIndex - 1, 0);
                activeItemKeyRef.current = getItemKeyAtIndex(visibleItems, nextIndex);
                return nextIndex;
            });
            return;
        }

        if (event.key === 'Enter') {
            if ((event.metaKey || event.ctrlKey) && query) {
                event.preventDefault();
                handleAskAi();
                return;
            }

            if (open && activeIndex >= 0 && visibleItems[activeIndex]) {
                event.preventDefault();
                handleSelectResult(visibleItems[activeIndex]);
                return;
            }

            if (query && isStrongAiQuestion) {
                event.preventDefault();
                handleAskAi();
            }

            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();

            if (open) {
                setOpen(false);
            } else {
                event.currentTarget.blur();
            }
        }
    };

    return {
        search,
        open,
        activeTab,
        activeIndex,
        dropdownId,
        tabPanelId,
        activeDescendantId,
        announcement,
        query,
        groups,
        totalResults,
        recentItems,
        visibleGroups,
        visibleItems,
        isLoading,
        error,
        showEmptyState,
        showFirstQuerySkeleton,
        showRefreshingProgress,
        showNoResults,
        handleOpenChange,
        handleAskAi,
        handleChange,
        handleClear,
        handleFocus,
        handleKeyDown,
        handleRetry,
        handleTabChange,
        handleRowHover,
        handleSelectItem,
    };
};

export interface UniversalSearchResultViewProps {
    activeTab: SearchTab;
    activeIndex: number;
    announcement: string;
    dropdownId: string;
    error?: Error | null;
    groups: SearchGroup[];
    query: string;
    recentItems: SearchResultItem[];
    showEmptyState?: boolean;
    showFirstQuerySkeleton?: boolean;
    showNoResults?: boolean;
    showRefreshingProgress?: boolean;
    tabPanelId: string;
    totalResults: number;
    visibleGroups: SearchGroup[];
    visibleItems: SearchSelectableItem[];
    className?: string;
    onAskAi?: () => void;
    onRetry?: () => void;
    onRowHover?: (index: number) => void;
    onSelectItem?: (item: SearchSelectableItem, index: number) => void;
    onTabChange?: (tab: SearchTab) => void;
}

export const UniversalSearchResultView = ({
    activeTab,
    activeIndex,
    announcement,
    dropdownId,
    error,
    groups,
    query,
    recentItems,
    showEmptyState,
    showFirstQuerySkeleton,
    showNoResults,
    showRefreshingProgress,
    tabPanelId,
    totalResults,
    visibleGroups,
    visibleItems,
    className,
    onAskAi,
    onRetry,
    onRowHover,
    onSelectItem,
    onTabChange,
}: UniversalSearchResultViewProps) => {
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const rowRefs = useRef<Record<string, HTMLLIElement | null>>({});

    useEffect(() => {
        if (activeIndex < 0) {
            return;
        }

        const activeItem = visibleItems[activeIndex];
        if (!activeItem) {
            return;
        }

        const container = scrollContainerRef.current;
        const row = rowRefs.current[getSearchOptionId(activeItem)];
        if (!container || !row) {
            return;
        }

        const edgePadding = 40;
        const rowTop = row.offsetTop;
        const rowBottom = rowTop + row.offsetHeight;
        const viewportTop = container.scrollTop;
        const viewportBottom = viewportTop + container.clientHeight;

        if (rowTop < viewportTop + edgePadding) {
            container.scrollTop = Math.max(0, rowTop - edgePadding);
        } else if (rowBottom > viewportBottom - edgePadding) {
            container.scrollTop = rowBottom - container.clientHeight + edgePadding;
        }
    }, [activeIndex, visibleItems]);

    return (
        <div
            id={dropdownId}
            className={cn(
                'border-border bg-popover @container overflow-hidden rounded-[20px] shadow-[0_16px_48px_-10px_rgba(54,42,22,0.2),0_3px_10px_rgba(54,42,22,0.06)]',
                className,
            )}
        >
            <span role="status" aria-live="polite" className="sr-only">
                {announcement}
            </span>

            <div className="border-border relative h-10 border-b bg-white px-5">
                <div className="flex h-full items-center justify-between gap-4">
                    <div className="flex items-center gap-2.5">
                        <span className="text-gold-300 inline-flex h-4 w-4 items-center justify-center rounded-full">
                            <LuSparkle className="h-2.5 w-2.5" />
                        </span>
                        <span className="text-cocoa-600 text-[11.5px] font-thin tracking-[0.22em] uppercase @max-[400px]:hidden">
                            Claro Intelligence
                        </span>
                    </div>
                    <span className="text-cocoa-500 text-[13px] font-thin">
                        {showEmptyState
                            ? 'Start typing to search clients, accounts, orgs…'
                            : `${totalResults} results for "${query}"`}
                    </span>
                </div>

                {showRefreshingProgress && (
                    <div className="bg-cocoa-100 absolute right-0 bottom-0 left-0 h-[2px] overflow-hidden">
                        <div className="bg-gold-300 h-full w-1/2 animate-pulse" />
                    </div>
                )}
            </div>

            {!showEmptyState && (
                <ResultTabs
                    activeTab={activeTab}
                    idPrefix={dropdownId}
                    groups={groups}
                    tabPanelId={tabPanelId}
                    totalResults={totalResults}
                    onTabChange={onTabChange}
                />
            )}

            <div
                ref={scrollContainerRef}
                role={showEmptyState ? undefined : 'tabpanel'}
                id={tabPanelId}
                aria-labelledby={showEmptyState ? undefined : `${dropdownId}-${activeTab}-search-tab`}
                className="max-h-[520px] overflow-y-auto"
            >
                {error ? (
                    <CenteredState
                        title="Something went wrong"
                        body="Retry"
                        actionLabel="Retry"
                        onAction={onRetry}
                        isButton
                    />
                ) : showEmptyState ? (
                    <EmptyState
                        recentItems={recentItems}
                        activeIndex={activeIndex}
                        onRowHover={onRowHover}
                        onSelectItem={onSelectItem}
                        rowRefs={rowRefs}
                    />
                ) : showFirstQuerySkeleton ? (
                    <LoadingSkeleton />
                ) : showNoResults ? (
                    <NoResultsState query={query} onAskAi={onAskAi} />
                ) : (
                    <>
                        {visibleGroups.map((group) => (
                            <ResultGroup
                                key={group.type}
                                group={group}
                                activeIndex={activeIndex}
                                activeTab={activeTab}
                                query={query}
                                rowRefs={rowRefs}
                                visibleItems={visibleItems}
                                onRowHover={onRowHover}
                                onSelectItem={onSelectItem}
                            />
                        ))}
                    </>
                )}
            </div>
            <Footer />
        </div>
    );
};

const ResultTabs = ({
    activeTab,
    idPrefix,
    groups,
    tabPanelId,
    totalResults,
    onTabChange,
}: {
    activeTab: SearchTab;
    idPrefix: string;
    groups: SearchGroup[];
    tabPanelId: string;
    totalResults: number;
    onTabChange?: (tab: SearchTab) => void;
}) => {
    const tabs: { tab: SearchTab; label: string; count: number; icon?: React.ReactNode }[] = [
        { tab: 'All', label: 'All', count: totalResults },
        { tab: 'Household', label: 'Households', count: getGroupForTab(groups, 'Household').total, icon: <BiHome /> },
        { tab: 'Person', label: 'Persons', count: getGroupForTab(groups, 'Person').total, icon: <BiUser /> },
        {
            tab: 'Organization',
            label: 'Organizations',
            count: getGroupForTab(groups, 'Organization').total,
            icon: <LuBuilding2 />,
        },
        {
            tab: 'Account',
            label: 'Accounts',
            count: getGroupForTab(groups, 'Account').total,
            icon: <RiAccountBoxLine />,
        },
    ];

    return (
        <div role="tablist" aria-label="Search result types" className="border-cocoa-100 bg-cocoa-0 border-b px-5 pt-2">
            <div className="flex flex-wrap items-end gap-6 @max-[700px]:gap-2">
                {tabs.map((tab) => {
                    const isDisabled = tab.count === 0;
                    const isActive = tab.tab === activeTab;
                    const isAllTab = tab.tab === 'All';

                    return (
                        <button
                            key={tab.tab}
                            id={`${idPrefix}-${tab.tab}-search-tab`}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            data-active={isActive}
                            aria-controls={tabPanelId}
                            aria-label={!isAllTab ? tab.label : undefined}
                            disabled={isDisabled}
                            title={!isAllTab ? tab.label : undefined}
                            className={cn(
                                'focus-visible:ring-cocoa-300 group/tab inline-flex h-9 cursor-pointer items-center gap-2',
                                'border-b-[2px] border-transparent px-0 pb-2 text-[13px] font-thin transition-colors',
                                'focus-visible:rounded-[6px] focus-visible:ring-[3px] focus-visible:outline-none',
                                !isAllTab &&
                                    '@max-[700px]:w-9 @max-[700px]:justify-center @max-[700px]:gap-0 @max-[700px]:data-[active=true]:w-auto @max-[700px]:data-[active=true]:justify-start @max-[700px]:data-[active=true]:gap-2',
                                isActive ? 'text-cocoa-900 border-b-gold-300' : 'text-cocoa-700 hover:text-cocoa-900',
                                isDisabled && 'hover:text-cocoa-700 cursor-default opacity-40',
                            )}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => onTabChange?.(tab.tab)}
                        >
                            {tab.icon ? <span className="text-cocoa-600 text-[15px]">{tab.icon}</span> : null}
                            {isAllTab ? (
                                <span>{tab.label}</span>
                            ) : (
                                <>
                                    <span className="sr-only">{tab.label}</span>
                                    <span className="@max-[700px]:hidden @max-[700px]:group-data-[active=true]/tab:inline">
                                        {tab.label}
                                    </span>
                                </>
                            )}
                            {
                                <span
                                    className={cn(
                                        'border-cocoa-100 text-cocoa-500 rounded-full border bg-white px-2 py-0.5 text-[11px] leading-none tabular-nums @max-[700px]:hidden',
                                        isActive && 'border-gold-100 text-gold-400',
                                    )}
                                >
                                    {tab.count}
                                </span>
                            }
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const ResultGroup = ({
    group,
    activeIndex,
    activeTab,
    query,
    rowRefs,
    visibleItems,
    onRowHover,
    onSelectItem,
}: {
    group: SearchGroup;
    activeIndex: number;
    activeTab: SearchTab;
    query: string;
    rowRefs: React.MutableRefObject<Record<string, HTMLLIElement | null>>;
    visibleItems: SearchSelectableItem[];
    onRowHover?: (index: number) => void;
    onSelectItem?: (item: SearchSelectableItem, index: number) => void;
}) => {
    const showGroupHeader = activeTab === 'All';
    const groupHeadingId = `claro-search-group-${group.type.toLowerCase()}`;
    const display = ENTITY_DISPLAY[group.type];

    return (
        <section>
            {showGroupHeader && (
                <div
                    id={groupHeadingId}
                    className="text-cocoa-500 border-cocoa-100 flex items-center gap-2.5 border-t px-5 pt-5 pb-3 first:border-t-0"
                >
                    <span className="text-[14px]">{display.icon}</span>
                    <span className="text-[11px] tracking-[0.22em] uppercase">{display.label}</span>
                    <span className="text-[11px] tracking-[0.08em]">{group.total}</span>
                </div>
            )}

            <ul
                role="listbox"
                aria-labelledby={showGroupHeader ? groupHeadingId : undefined}
                aria-label={!showGroupHeader ? `${group.type} results` : undefined}
                className="pb-1"
            >
                {group.items.map((item) => {
                    const visibleIndex = visibleItems.findIndex(
                        (visibleItem) => visibleItem.kind === 'result' && visibleItem.id === item.id,
                    );
                    const optionId = getSearchOptionId(item);

                    return (
                        <ResultRow
                            key={item.id}
                            active={visibleIndex === activeIndex}
                            item={item}
                            optionId={optionId}
                            query={query}
                            ref={(node) => {
                                rowRefs.current[optionId] = node;
                            }}
                            onMouseEnter={() => onRowHover?.(visibleIndex)}
                            onSelect={() => onSelectItem?.(item, visibleIndex)}
                        />
                    );
                })}
            </ul>
        </section>
    );
};

const EmptyState = ({
    recentItems,
    activeIndex,
    rowRefs,
    onRowHover,
    onSelectItem,
}: {
    recentItems: SearchResultItem[];
    activeIndex: number;
    rowRefs: React.MutableRefObject<Record<string, HTMLLIElement | null>>;
    onRowHover?: (index: number) => void;
    onSelectItem?: (item: SearchSelectableItem, index: number) => void;
}) => {
    const items = [...recentItems];

    return (
        <div className="py-2">
            <EmptySection
                title="Recent"
                items={recentItems}
                activeIndex={activeIndex}
                offset={0}
                rowRefs={rowRefs}
                onRowHover={onRowHover}
                onSelectItem={onSelectItem}
            />

            {items.length === 0 && (
                <CenteredState
                    title="Start typing to search"
                    body="Search households, people, organizations, or accounts."
                />
            )}
        </div>
    );
};

const EmptySection = ({
    title,
    items,
    activeIndex,
    offset,
    rowRefs,
    onRowHover,
    onSelectItem,
}: {
    title: string;
    items: SearchSelectableItem[];
    activeIndex: number;
    offset: number;
    rowRefs: React.MutableRefObject<Record<string, HTMLLIElement | null>>;
    onRowHover?: (index: number) => void;
    onSelectItem?: (item: SearchSelectableItem, index: number) => void;
}) => {
    if (items.length === 0) {
        return null;
    }

    return (
        <section>
            <div className="text-cocoa-500 border-cocoa-100 border-t px-5 pt-5 pb-3 text-[11px] tracking-[0.22em] uppercase first:border-t-0">
                {title}
            </div>
            <ul role="listbox" aria-label={title} className="pb-1">
                {items.map((item, index) => {
                    const visibleIndex = offset + index;
                    const optionId = getSearchOptionId(item);

                    return (
                        <ResultRow
                            key={`${item.kind}-${item.id}`}
                            active={visibleIndex === activeIndex}
                            item={item}
                            optionId={optionId}
                            query=""
                            ref={(node) => {
                                rowRefs.current[optionId] = node;
                            }}
                            onMouseEnter={() => onRowHover?.(visibleIndex)}
                            onSelect={() => onSelectItem?.(item, visibleIndex)}
                        />
                    );
                })}
            </ul>
        </section>
    );
};

const LoadingSkeleton = () => (
    <div className="py-2">
        {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 px-5 py-[7px]">
                <div className="bg-cocoa-100 h-[26px] w-[26px] animate-pulse rounded-full" />
                <div className="flex-1">
                    <div className="bg-cocoa-100 h-3.5 w-2/3 animate-pulse rounded-full" />
                </div>
            </div>
        ))}
    </div>
);

const NoResultsState = ({ query, onAskAi }: { query: string; onAskAi?: () => void }) => (
    <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 px-8 py-10 text-center">
        <div className="space-y-1">
            <h3 className="text-cocoa-900 text-[15px] font-medium">No results for &quot;{query}&quot;</h3>
            <p className="text-cocoa-500 max-w-[320px] text-[13.5px] font-thin">
                Try a different spelling, or search by name, email, GUID, or live custody account number.
            </p>
        </div>
        <button
            type="button"
            className="text-cocoa-900 border-gold-100 bg-gold-0 hover:bg-gold-50 focus-visible:ring-gold-100 inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-[13px] font-thin transition-colors focus-visible:ring-[3px] focus-visible:outline-none"
            aria-label={`Ask Claire about ${query}`}
            onClick={onAskAi}
        >
            <LuSparkle className="text-gold-300 h-3.5 w-3.5" />
            Ask Claire&#8482;
            <span className="text-cocoa-500 inline-flex items-center gap-0.5">
                <span className="text-[10px]">⌘</span>
                <LuCornerDownLeft className="h-2.5 w-2.5" />
            </span>
        </button>
    </div>
);

const CenteredState = ({
    title,
    body,
    actionLabel,
    isButton,
    onAction,
}: {
    title: string;
    body: string;
    actionLabel?: string;
    isButton?: boolean;
    onAction?: () => void;
}) => (
    <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 px-8 py-10 text-center">
        <h3 className="text-cocoa-900 text-[15px] font-medium">{title}</h3>
        {isButton && actionLabel ? (
            <button
                type="button"
                className="text-gold-400 border-cocoa-100 hover:bg-cocoa-0 focus-visible:ring-cocoa-300 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[13px] font-thin transition-colors focus-visible:ring-[3px] focus-visible:outline-none"
                onClick={onAction}
            >
                <LuRefreshCw className="h-3.5 w-3.5" />
                {actionLabel}
            </button>
        ) : (
            <p className="text-cocoa-500 max-w-[280px] text-[13.5px] font-thin">{body}</p>
        )}
    </div>
);

const Footer = () => (
    <div className="text-cocoa-500 border-cocoa-100 bg-cocoa-0 flex h-10 items-center justify-between gap-4 border-t px-5 text-[12px]">
        <div className="flex items-center gap-4">
            <FooterHint icon={<LuArrowUpDown className="h-3 w-3" />} label="navigate" />
            <FooterHint icon={<LuCornerDownLeft className="h-3 w-3" />} label="select" />
            <FooterHint icon={<span className="text-[10px] font-medium lowercase">esc</span>} label="close" />
        </div>
        <span className="inline-flex items-center gap-1.5 font-thin @max-[530px]:hidden">
            <LuSparkle className="text-gold-300 h-3 w-3" />
            Powered by Claro Intelligence
        </span>
    </div>
);

const FooterHint = ({ icon, label }: { icon: React.ReactNode; label: string }) => (
    <span className="inline-flex items-center gap-2">
        <span className="text-cocoa-500 border-cocoa-100 inline-flex min-w-7 items-center justify-center rounded-[6px] border bg-white px-1.5 py-0.5">
            {icon}
        </span>
        <span>{label}</span>
    </span>
);

const ResultRow = memo(
    React.forwardRef<
        HTMLLIElement,
        {
            active: boolean;
            item: SearchSelectableItem;
            optionId: string;
            query: string;
            onMouseEnter: () => void;
            onSelect: () => void;
        }
    >(({ active, item, optionId, query, onMouseEnter, onSelect }, ref) => {
        const avatarShapeClass =
            item.kind === 'suggested' ? 'text-gold-400 bg-gold-0 rounded-full' : getAvatarClassName(item.type);

        return (
            <li
                id={optionId}
                ref={ref}
                role="option"
                aria-selected={active}
                className={cn(
                    'border-cocoa-100 flex h-10 cursor-pointer items-center gap-3 border-b border-l-[3px] border-l-transparent px-5 transition-colors last:border-b-0',
                    active && 'border-l-gold-300 bg-cocoa-0',
                )}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={onMouseEnter}
                onClick={onSelect}
            >
                <div
                    className={cn(
                        'flex h-[24px] w-[24px] shrink-0 items-center justify-center text-[11px] font-thin uppercase',
                        avatarShapeClass,
                    )}
                >
                    {item.kind === 'suggested' ? '?' : item.initials}
                </div>

                <div className="min-w-0 flex-1 text-left">
                    <div className="truncate text-[14px] leading-[1.3]">
                        <span className="text-gold-400 font-medium">{renderHighlightedLabel(item.name, query)}</span>
                    </div>
                </div>

                <div className="flex w-7 justify-end">
                    {active ? (
                        <span className="text-cocoa-500 border-cocoa-100 inline-flex h-6 w-6 items-center justify-center rounded-[6px] border bg-white">
                            <LuCornerDownLeft className="h-3 w-3" />
                        </span>
                    ) : null}
                </div>
            </li>
        );
    }),
);

ResultRow.displayName = 'ResultRow';

const getAvatarClassName = (type: SearchEntityType) => {
    switch (type) {
        case 'Organization':
            return 'bg-cocoa-100 text-cocoa-600 rounded-[10px]';
        case 'Account':
            return 'bg-gold-0 text-cocoa-600 rounded-[10px]';
        default:
            return 'bg-cocoa-100 text-cocoa-600 rounded-full';
    }
};

const renderHighlightedLabel = (label: string, query: string) => {
    const tokens = Array.from(
        new Set(
            query
                .trim()
                .split(/\s+/)
                .map((token) => token.trim())
                .filter(Boolean),
        ),
    );

    if (tokens.length === 0) {
        return label;
    }

    const pattern = new RegExp(`(${tokens.map(escapeForRegex).join('|')})`, 'gi');
    const parts = label.split(pattern).filter(Boolean);
    const tokenSet = new Set(tokens.map((token) => token.toLowerCase()));

    return parts.map((part, index) =>
        tokenSet.has(part.toLowerCase()) ? (
            <mark key={`${part}-${index}`} className="bg-gold-100 rounded-[3px] px-0.5 text-inherit">
                {part}
            </mark>
        ) : (
            <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
        ),
    );
};

const escapeForRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
